import { useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import SvgIcon from "../../../parts/SvgIcon";
import FlexibleProgramManager from "./components/FlexibleProgramManager";
import FlexibleSettings from "./components/FlexibleSettings";
import FlexibleStop from "./components/FlexibleStop";
import ImportTemplateModal from "./components/ImportTemplateModal";
import WeekStartModal from "./components/WeekStartModal";

/**
 * One client's Flexible Program.
 *
 * Deliberately leaner than the Team manager it is modelled on: no tab bar, no
 * Invite, no Publish, no Members and no Team Information. All of those assume
 * more than one person is looking at the program, and this one is 1:1 - it is
 * live for its client from the moment it is created.
 */
const FlexibleProgram = ({ locals, api }) => {
  const { t } = useTranslation("coaching-portal");
  const cycle = locals?.editCycle;
  const athlete = locals?.query?.athlete;
  // backTo lets a caller (e.g. the Calendar's Flexible Programming chip)
  // route the back button to the exact month/week view the coach came from,
  // instead of always landing on the athlete's Programs page.
  const backPath =
    locals?.query?.backTo ||
    `/athlete-portal/programs${athlete ? `?athlete=${athlete}${locals?.query?.organizationCoach ? `&organizationCoach=${locals.query.organizationCoach}` : ""}` : ""}`;

  // Settings and Stop are local-state modals, not the usual
  // ?modal[type]=...-driven ones (see FlexibleSettings/FlexibleStop): opening
  // or closing them never re-runs flexible-program.controller.js. Only
  // actually saving/stopping talks to the server, same as before.
  const [activeModal, setActiveModal] = useState(null); // null | "settings" | "stop" | "add-week" | "import-template"

  const addWeek = (startDate) => {
    submit(`/actions/cycle/${cycle._id}/flexible-add-week`, {
      method: "POST",
      body: { athlete, ...(startDate ? { startDate } : {}) },
    });
  };

  return (
    <main className="teams-manager flexible-manager">
      <header>
        <hgroup>
          <a href={backPath}>
            <button type="button" data-button-type="tertiary" data-icon-only>
              <SvgIcon src="/img/new-icons/arrow-square-left.svg" />
            </button>
            <span className="text-nowrap pr-4">{cycle?.name}</span>
          </a>
        </hgroup>

        <hgroup>
          {/* biome-ignore lint/a11y/useSemanticElements: a real link so Rill navigates; role only picks up button styles */}
          <a
            role="button"
            data-button-type="secondary"
            data-icon
            data-icon-only
            title={t("flexible.preview.action")}
            href={`/athlete-portal/programs/flexible-program-preview/${cycle?._id}${athlete ? `?athlete=${athlete}` : ""}`}
          >
            <SvgIcon
              src="/img/new-icons/eye.svg"
              alt={t("flexible.preview.action")}
            />
          </a>

          <button
            type="button"
            data-button-type="secondary"
            data-icon
            data-icon-only
            onClick={() => setActiveModal("settings")}
          >
            <SvgIcon
              src="/img/new-icons/settings.svg"
              alt={t("flexible.settings.action")}
            />
          </button>

          <button
            type="button"
            data-button-type="outline"
            data-action="delete"
            onClick={() => setActiveModal("stop")}
          >
            {t("flexible.stop.action")}
          </button>

          <button
            type="button"
            data-button-type="outline"
            data-icon="import"
            onClick={() => setActiveModal("import-template")}
          >
            <img alt="" src="/img/new-icons/import-gradient-icon.svg" />
            {t("flexible.importTemplate.action")}
          </button>

          {/* Always confirms a start date first - the picker lists weeks
              well beyond just the next boundary (see WeekStartModal), so
              even a non-repeating live week has real choices worth seeing,
              not just one obvious next step to fall back on silently. */}
          <button
            type="button"
            data-button-type="primary"
            onClick={() => setActiveModal("add-week")}
          >
            <img alt={t("flexible.addAlt")} src="/img/new-icons/add.svg" />
            {t("flexible.addNewWeek")}
          </button>
        </hgroup>
      </header>

      <div className="team-tab-container">
        <FlexibleProgramManager
          locals={locals}
          api={api}
          onImport={() => setActiveModal("import-template")}
        />
      </div>

      {activeModal === "settings" && (
        <FlexibleSettings
          cycle={cycle}
          athlete={athlete}
          onClose={() => setActiveModal(null)}
        />
      )}
      {activeModal === "stop" && (
        <FlexibleStop
          cycleId={cycle._id}
          cycleName={cycle.name}
          athlete={athlete}
          onClose={() => setActiveModal(null)}
        />
      )}
      {activeModal === "add-week" && (
        <WeekStartModal
          cycle={cycle}
          title={t("flexible.weekStart.addTitle")}
          onClose={() => setActiveModal(null)}
          onConfirm={(startDate) => {
            setActiveModal(null);
            addWeek(startDate);
          }}
        />
      )}
      {activeModal === "import-template" && (
        <ImportTemplateModal
          cycle={cycle}
          athlete={athlete}
          templates={locals?.athleteCycles}
          api={api}
          onClose={() => setActiveModal(null)}
        />
      )}
    </main>
  );
};

export default FlexibleProgram;
